import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { useFieldState } from 'informed';
import { ProgressBar } from 'components/LinearProgressBar';
import { ProgressBarMode } from 'components/LinearProgressBar/helpers';

interface PasswordStrengthCheckerProps {
  field: string;
}

export const PasswordStrengthChecker: FC<PasswordStrengthCheckerProps> = ({
  field,
}) => {
  const { t } = useTranslation('validation');

  const password = useFieldState<string | undefined>(field);

  const { isShort, isLong } = useMemo(() => {
    const length = password?.value?.length ?? 0;
    return {
      isShort: length < 8,
      isLong: length > 30,
    };
  }, [password]);

  const isValidLength = !isShort && !isLong;

  return (
    <ScPasswordStrengthChecker>
      <ProgressBar
        mode={isValidLength ? ProgressBarMode.SUCCESS : ProgressBarMode.ERROR}
        height={4}
        progressMinWidth={isValidLength ? '0px' : '80px'}
        value={Number(isValidLength ? 100 : 0)}
        padding={0}
        borderRadius="50%"
      />
      <ScStrengthItem isActive={isValidLength}>
        {isValidLength
          ? t('strongPassword')
          : isLong
          ? t('invalidPasswordLong')
          : t('invalidPasswordShort')}
      </ScStrengthItem>
    </ScPasswordStrengthChecker>
  );
};

const ScPasswordStrengthChecker = styled.div`
  margin: 8px 16px 0 16px;
  position: relative;
  z-index: 2;
  left: 0;
  right: 0;
`;

interface ScStrengthItemProps {
  isActive: boolean;
}

const ScStrengthItem = styled.div<ScStrengthItemProps>`
  display: block;
  position: relative;
  font-size: 12px;
  line-height: 18px;
  margin-top: 4px;

  color: ${({ isActive, theme }) => {
    if (isActive) {
      return theme.greenColor;
    } else {
      return theme.pinkColor;
    }
  }};
`;
